import { captureException } from '@sentry/react';
import { useGlobal } from './useGlobal';

export const WIDGET_API_PREFIX = { v1: 'api/widget/v1' };

/**
 * Route prefix on the rw26 v2 widget service. Deliberately not an entry in
 * WIDGET_API_PREFIX: v2 is served by a *different host* than `apiBaseUrl`, so it
 * cannot be selected by swapping a path segment the way a same-host version bump
 * could. See useV2APIRoute.
 */
export const WIDGET_V2_API_PREFIX = 'widget/v2';

const stripTrailingSlashes = (url: string) => url.replace(/\/*$/, '');

export const useAPIRoute = (
    route: string,
    version: keyof typeof WIDGET_API_PREFIX = 'v1'
) => {
    const { apiBaseUrl } = useGlobal();

    if (!apiBaseUrl?.length) {
        captureException(new Error(`No API base URL - cannot contact server`));
        throw new Error(`Cannot contact server`);
    }

    const normalizedBaseUrl = stripTrailingSlashes(apiBaseUrl);

    return `${normalizedBaseUrl}/${WIDGET_API_PREFIX[version]}/${route}`;
};

/**
 * Build a URL for the v2 widget service, or return undefined when v2 is not
 * available for this account (flag off, or no service URL was threaded in).
 *
 * Undefined rather than throwing — unlike v1, v2 is optional: every caller keeps
 * its v1 path as a fallback, so an unconfigured v2 is a normal state, not an
 * error worth reporting to Sentry.
 *
 * Leading slashes on `route` are stripped. v1 callers pass both conventions
 * ('config' and '/serviceareas/list'), and the v1 template preserves the
 * resulting double slash; POSTing to a doubled path on the v2 service would
 * 404 rather than be silently tolerated.
 */
export const useV2APIRoute = (route: string): string | undefined => {
    const { v2ServiceUrl, useV2Data } = useGlobal();

    if (!useV2Data || !v2ServiceUrl?.length) {
        return undefined;
    }

    return `${stripTrailingSlashes(
        v2ServiceUrl
    )}/${WIDGET_V2_API_PREFIX}/${route.replace(/^\/+/, '')}`;
};
